import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqData = [
  {
    category: 'General',
    questions: [
      {
        q: 'Are you a legally ordained minister?',
        a: 'Yes, I am a legally ordained minister authorized to perform wedding ceremonies. All marriages I officiate are legally recognized.',
      },
      {
        q: 'What types of ceremonies do you perform?',
        a: 'I perform weddings, vow renewals, commitment ceremonies, and elopements. I welcome couples of all backgrounds, faiths, and orientations.',
      },
      {
        q: 'Do you perform religious, spiritual, or secular ceremonies?',
        a: 'I am comfortable performing all types of ceremonies. Whether you want a faith-based ceremony, a spiritual but non-denominational celebration, or a completely secular ceremony, I can accommodate your wishes.',
      },
    ],
  },
  {
    category: 'Booking & Availability',
    questions: [
      {
        q: 'How far in advance should I book?',
        a: 'I recommend booking at least 2-3 months in advance for wedding ceremonies, though I can sometimes accommodate shorter timelines. For elopements, I can often accommodate requests with just a few weeks notice.',
      },
      {
        q: 'Do you travel for ceremonies?',
        a: 'Yes! I am available to travel to your venue location. Travel fees may apply for distances beyond 30 miles.',
      },
      {
        q: 'Are you available on weekdays?',
        a: 'Yes, I have availability on weekdays and may offer reduced rates for Monday-Thursday ceremonies.',
      },
    ],
  },
  {
    category: 'Ceremony Details',
    questions: [
      {
        q: 'Will you help write the ceremony script?',
        a: 'Absolutely! I work closely with each couple to create a personalized ceremony script that reflects your relationship, values, and vision for your special day.',
      },
      {
        q: 'Can we write our own vows?',
        a: 'Yes, and I encourage it! I offer vow writing guidance and can provide examples and prompts to help you craft meaningful personal vows.',
      },
      {
        q: 'What is a unity ceremony?',
        a: 'A unity ceremony is a symbolic ritual during your wedding that represents the joining of two lives. Popular options include candle lighting, sand ceremonies, handfasting, or wine blending. I can help you choose one that resonates with you.',
      },
      {
        q: 'How long is a typical ceremony?',
        a: 'Most ceremonies last 15-30 minutes, though this can vary based on your preferences, readings, unity ceremonies, and other elements you wish to include.',
      },
    ],
  },
  {
    category: 'Legal Requirements',
    questions: [
      {
        q: 'What do we need for a legal marriage?',
        a: 'You will need a valid marriage license from your county. I will sign the license after the ceremony and ensure it is properly filed. Requirements vary by state, so I recommend checking with your local county clerk.',
      },
      {
        q: 'Do you handle the marriage license paperwork?',
        a: 'Yes, signing the marriage license is included in all packages. I ensure all documentation is completed correctly and can provide guidance on the filing process.',
      },
      {
        q: 'Can you be a witness for our elopement?',
        a: 'In states that require witnesses, I can serve as one of your witnesses or help arrange for witnesses if needed.',
      },
    ],
  },
  {
    category: 'Pricing & Payments',
    questions: [
      {
        q: 'What are your rates?',
        a: 'Packages start at $350 for essential services and range to $850+ for premium packages. Custom packages are also available. Pricing depends on the type of ceremony, location, and services needed.',
      },
      {
        q: 'Do you require a deposit?',
        a: 'Yes, a 50% deposit is required to secure your date, with the remaining balance due one week before the ceremony.',
      },
      {
        q: 'What forms of payment do you accept?',
        a: 'I accept various payment methods including credit/debit cards and bank transfers. Payment details will be provided upon booking.',
      },
    ],
  },
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIdx, questionIdx) => {
    const key = `${categoryIdx}-${questionIdx}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="pt-20" data-testid="faq-page">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            Questions & Answers
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-warm-100 mb-6">
            Frequently Asked Questions
          </h1>
          <div className="divider-gold max-w-xs mx-auto" />
          <p className="text-warm-100/70 text-lg max-w-2xl mx-auto mt-6">
            Find answers to common questions about our services, booking process, and what to expect.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section-container">
        <div className="max-w-3xl mx-auto">
          {faqData.map((category, categoryIdx) => (
            <div key={categoryIdx} className="mb-12">
              <h2 className="text-2xl font-serif text-gold-500 mb-6">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((item, questionIdx) => {
                  const key = `${categoryIdx}-${questionIdx}`;
                  const isOpen = openItems[key];
                  
                  return (
                    <div
                      key={questionIdx}
                      className="card-elegant overflow-hidden"
                      data-testid={`faq-item-${categoryIdx}-${questionIdx}`}
                    >
                      <button
                        onClick={() => toggleItem(categoryIdx, questionIdx)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left"
                      >
                        <span className="text-warm-100 font-medium pr-4">
                          {item.q}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-gold-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gold-500 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4 animate-fade-in">
                          <p className="text-warm-100/70 leading-relaxed">
                            {item.a}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="max-w-2xl mx-auto text-center mt-16">
          <h3 className="text-2xl font-serif text-warm-100 mb-4">
            Still Have Questions?
          </h3>
          <p className="text-warm-100/70 mb-6">
            We're happy to answer any questions you may have about our services or your upcoming ceremony.
          </p>
          <a
            href="/contact"
            className="btn-gold text-sm tracking-[0.2em] uppercase"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
